import { connect } from 'react-redux';
import { RootState } from '@iwProvider/configureStore';
import { getTopUpStatus } from '../NewOutstandingView.helpers';
import { TProductType } from '../../../dashboard/Dashboard.types';
import { NotYetTopUpEligibleAlert } from './Alerts/NotYetTopUpEligibleAlert';
import { TopUpPendingAlert } from './Alerts/TopUpPendingAlert';
import { TopUpEligibleAlert } from './Alerts/TopUpEligibleAlert';
import { TopUpApprovedAlert } from './Alerts/TopUpApprovedAlert';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import {
    getAccountsQueryConfig,
    getSplitTestQueryConfig
} from '@api/lendingAPI/queryConfigs';
import { tracking } from '@iwoca/frontend-tracking-library';
import { useEffect } from 'react';
import { getCountry } from '@iwIntlProvider/CountryProvider';

const mapStateToProps = (state: RootState, { productType }: TProps) => {
    return {
        topUpStatus: getTopUpStatus(state, productType)
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;
type TProps = {
    accountId: string;
    productType: TProductType;
    onAccessFundsClick: () => void;
    redirectToApplyFlexiLoan: () => void;
};

const TopUpStatusAlertBoxes = ({
    accountId,
    topUpStatus,
    onAccessFundsClick,
    redirectToApplyFlexiLoan
}: TProps & PropsFromRedux) => {
    const stateKey = useStateKey();

    const { data: accountsResponse } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: (data) => data.data,
        suspense: true
    });

    const { data: applicationResponse } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey }),
        select: (data) => data.data,
        suspense: true
    });

    const { data: branch } = useQuery({
        ...getSplitTestQueryConfig(stateKey, 'topup_banner_redesign_q4_2024'),
        select: (data) => data.data.branch,
        suspense: true,
        enabled: getCountry() === 'UK'
    });

    useEffect(() => {
        if (branch) {
            tracking.ampli.dashboardBannerViewed({
                account_id: accountId,
                // @ts-expect-error
                topup_status: topUpStatus,
                topup_banner_redesign_test: branch
            });
        }
    }, [branch, topUpStatus, accountId]);

    const account = accountsResponse!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const application = applicationResponse!;

    switch (topUpStatus) {
        case 'NOT_YET_TOPUP_ELIGIBLE': {
            const amountToRepayBeforeTopUp =
                account.topup_eligibility!.amount_to_repay_before_topup;

            return (
                <NotYetTopUpEligibleAlert
                    amountToRepayBeforeTopUp={amountToRepayBeforeTopUp}
                />
            );
        }
        case 'TOPUP_PENDING': {
            const requestedAmount = application.requested_products![0].amount;

            const requestedCashAmount =
                application.requested_products![0].requested_cash_amount;

            return (
                <TopUpPendingAlert
                    requestedAmount={requestedAmount}
                    requestedCashAmount={requestedCashAmount}
                />
            );
        }
        case 'TOPUP_ELIGIBLE':
            return (
                <TopUpEligibleAlert
                    onApplyNowClick={() => {
                        tracking.ampli.topupEligibleBannerCtaClicked({
                            account_id: accountId
                        });
                        redirectToApplyFlexiLoan();
                    }}
                    branch={branch!}
                />
            );
        case 'TOPUP_APPROVED':
        case 'PRE_APPROVED': {
            const approvalAmount = account.latest_formal_offer.available_cash!;
            const timeoutDate = account.latest_formal_offer.timeout!;
            const interestRate =
                account.latest_formal_offer.interest_rates![0].rate!;

            return (
                <TopUpApprovedAlert
                    approvalAmount={approvalAmount}
                    timeoutDate={timeoutDate}
                    interestRate={interestRate}
                    onAccessFundsClick={() => {
                        onAccessFundsClick();
                        tracking.ampli.topupApprovedBannerCtaClicked({
                            account_id: accountId
                        });
                    }}
                    branch={branch!}
                />
            );
        }
        default:
            return null;
    }
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(TopUpStatusAlertBoxes);
